/**
 * Inspector command bodies (W2-06). Pure `(tx, args) => result` functions in the style of core/commands: they
 * compose `Core` bodies (which keep every model invariant) for the inspector's multi-item and unit-aware edits.
 * The UI runs them inside `DocStore.transact` / gestures (tx.ts); tests run them with the real TxDraft.
 *
 *  - Per-item expressions in FRIENDLY units (opacity %, width %, judge width %, Length %...): the user types
 *    `+=10` into a % field, so evaluation happens in the shown unit, in the same item order and with the same
 *    variables as Core's raw-unit `exprNotes` / `exprKeys`, then each value is written back in raw units.
 *  - "Set" on a line property (Length, Thickness, Size, Brush): no track -> one key at beat 0 (static value);
 *    one key -> that value for the whole chart; animated -> a keyframe at the playhead (the gizmo's key rule).
 *  - N LINES: name patterns, draw-order expressions, parents, flags, tags, textures and "Set at playhead"
 *    (`insertKeysAt` on every line) in one transaction.
 */

import { Core } from '../../core/commands/index';
import { setKeySide } from '../../core/commands/keys';
import type { Cmd, LineTypeChoice } from '../../core/commands/types';
import { Expr } from '../../core/expr/index';
import type { ExprProgram, ExprScope } from '../../core/expr/types';
import { isGifAsset, kindOfLine } from '../../core/model/lineKind';
import { PROP_SPECS, propAppliesTo } from '../../core/model/props';
import { fromFriendly, toFriendly } from '../../core/model/units';
import type {
  AssetId,
  FriendlyUnit,
  KeyId,
  Line,
  LineEditorData,
  LineId,
  LineProp,
  Note,
  NoteId,
  TrackValue,
  UiElement,
} from '../../core/model/types';
import type { KeyValuePart } from '../../core/commands/types';
import type { RecordPatch, TxBuilder } from '../../core/ops/types';
import { Beats } from '../../core/time/beat';
import type { Beat, Sec } from '../../core/time/types';
import {
  applyNamePattern,
  clampPreviewRange,
  isImageAssetRef,
  keyAt,
  lineTypeOf,
  notesInTimeOrder,
  previewRangeIssue,
  tidy,
  trackKeys,
} from './model';
import { m } from './inspector.messages';

/** Note fields edited in friendly units. */
export type NoteUnitField = 'size' | 'alpha' | 'judgeArea';

/** Shown value of a raw note field (width %, opacity %, judge width %). */
export function noteFieldToShown(field: NoteUnitField, raw: number): number {
  switch (field) {
    case 'size':
    case 'judgeArea':
      return tidy(raw * 100);
    case 'alpha':
      return tidy((raw * 100) / 255);
  }
}

/** Raw value of a shown note field, clamped to the model's range. */
export function noteFieldFromShown(field: NoteUnitField, shown: number): number {
  switch (field) {
    case 'size':
      return tidy(shown / 100);
    case 'judgeArea':
      return Math.max(0.01, tidy(shown / 100));
    case 'alpha':
      return Math.min(255, Math.max(0, Math.round((shown * 255) / 100)));
  }
}

function rawNoteField(n: Note, field: NoteUnitField): number {
  return field === 'judgeArea' ? (n.judgeArea ?? 1) : n[field];
}

function failExpr(tx: TxBuilder, index: number, count: number): never {
  return tx.fail({ key: m.exprFailed, params: { index: index + 1, count } });
}

/**
 * Per-note expression on a friendly-unit field (time order t, x, id; variables v i n u t s x speed size as in
 * Core.notes.exprNotes). Returns how many notes changed.
 */
export const exprNotesShown: Cmd<
  { readonly ids: readonly NoteId[]; readonly field: NoteUnitField; readonly prog: ExprProgram; readonly seed: number },
  number
> = (tx, { ids, field, prog, seed }) => {
  const notes = notesInTimeOrder(ids.map((id) => tx.doc.notes.get(id)).filter((n): n is Note => n !== undefined));
  const n = notes.length;
  const rng = Expr.rng(seed);
  let changed = 0;
  notes.forEach((note, i) => {
    const scope: ExprScope = {
      v: noteFieldToShown(field, rawNoteField(note, field)),
      i,
      n,
      u: n > 1 ? i / (n - 1) : 0,
      t: Beats.toF64(note.t),
      s: tx.tempo.secAt(note.t),
      x: note.x,
      speed: note.speed,
      size: note.size,
    };
    const out = Expr.apply(prog, scope, rng, tx.tempo);
    if (out === null || !Number.isFinite(out)) failExpr(tx, i, n);
    const raw = noteFieldFromShown(field, out);
    if (raw === rawNoteField(note, field) && (field !== 'judgeArea' || note.judgeArea !== null)) return;
    Core.notes.patchNotes(tx, { ids: [note.id], patch: { [field]: raw } });
    changed++;
  });
  return changed;
};

/**
 * Per-key expression in a friendly unit (keys in time order; variables v i n u t s, v0 = previous key's leave
 * value, the `v` speed suffix uses the previous segment's duration, as Core.keys.exprKeys). Writes through
 * Core.keys.setKeyValues (continuity, hold invariant, link groups).
 */
export const exprKeysShown: Cmd<
  {
    readonly keys: readonly KeyId[];
    readonly part: KeyValuePart;
    readonly unit: FriendlyUnit;
    readonly prog: ExprProgram;
    readonly seed: number;
    /**
     * UX-033 (CR-W3-05-1): write one side with the continuity rule of `setKeySide` (a continuous key moves whole,
     * a jump key keeps its other side) instead of `setKeyValues`. The inspector's From / To fields pass it.
     */
    readonly keepSide?: 'in' | 'out';
  },
  number
> = (tx, { keys, part, unit, prog, seed, keepSide }) => {
  const items = [...new Set(keys)]
    .map((id) => tx.doc.keys.get(id))
    .filter(
      (k): k is NonNullable<typeof k> => k !== undefined && typeof k.vOut === 'number' && typeof k.vIn === 'number',
    )
    .sort((a, b) => Beats.cmp(a.t, b.t) || a.id - b.id);
  const n = items.length;
  if (n === 0) tx.fail({ key: m.exprNoNumbers });
  const rng = Expr.rng(seed);
  // Values are computed first (every scope sees the values before the edit), then written.
  const edits: Array<readonly [KeyId, number]> = [];
  items.forEach((k, i) => {
    const raw = (part === 'in' ? k.vIn : k.vOut) as number;
    const prevId = tx.idx.prevKey(k.id);
    const prev = prevId === undefined ? undefined : tx.doc.keys.get(prevId);
    const s = tx.tempo.secAt(k.t);
    const scope: ExprScope = {
      v: tidy(toFriendly(unit, raw)),
      i,
      n,
      u: n > 1 ? i / (n - 1) : 0,
      t: Beats.toF64(k.t),
      s,
      v0: typeof prev?.vOut === 'number' ? tidy(toFriendly(unit, prev.vOut)) : tidy(toFriendly(unit, raw)),
      durSec: prev === undefined ? 0 : s - tx.tempo.secAt(prev.t),
    };
    const out = Expr.apply(prog, scope, rng, tx.tempo);
    if (out === null || !Number.isFinite(out)) failExpr(tx, i, n);
    edits.push([k.id, tidy(fromFriendly(unit, out))]);
  });
  let changed = 0;
  for (const [id, value] of edits) {
    const before = tx.doc.keys.get(id);
    if (before === undefined) continue;
    const cur = part === 'in' ? before.vIn : before.vOut;
    if (cur === value && (part !== 'both' || before.vIn === value)) continue;
    if (keepSide !== undefined) setKeySide(tx, { keys: [id], side: keepSide, value });
    else Core.keys.setKeyValues(tx, { keys: [id], part, value });
    changed++;
  }
  return changed;
};

/**
 * Sets a line property the inspector shows as one number (Length, Thickness, Size, Brush...). No track: a static
 * track (one key at beat 0). One key: that key's value (whole chart). Animated: the key at `at`, inserted
 * (shape-preserving) when there is none. Returns what happened, for the field's hint.
 */
export const setLineValue: Cmd<
  {
    readonly line: LineId;
    readonly prop: LineProp;
    readonly layer: number;
    readonly value: TrackValue;
    readonly at: Beat;
  },
  'created' | 'static' | 'keyed'
> = (tx, { line, prop, layer, value, at }) => {
  const l = PROP_SPECS[prop].layered ? layer : 0;
  const trackId = tx.idx.track(line, prop, l);
  if (trackId === undefined) {
    Core.keys.ensureTrack(tx, { owner: { kind: 'line', line, prop, layer: l }, value });
    return 'created';
  }
  const keys = trackKeys(tx.doc, tx.idx, trackId);
  const only = keys.length === 1 ? keys[0] : undefined;
  if (only !== undefined) {
    Core.keys.setKeyValues(tx, { keys: [only.id], part: 'both', value });
    return 'static';
  }
  const existing = keyAt(keys, at);
  if (existing !== null) {
    Core.keys.setKeyValues(tx, { keys: [existing.id], part: 'both', value });
  } else {
    Core.keys.insertKey(tx, { track: trackId, t: at, value });
  }
  return 'keyed';
};

/** N LINES rename: `pattern` applied per line in the given (export) order. Returns how many names changed. */
export const renameLines: Cmd<{ readonly lines: readonly LineId[]; readonly pattern: string }, number> = (
  tx,
  { lines, pattern },
) => {
  let changed = 0;
  lines.forEach((id, i) => {
    const line = tx.doc.lines.get(id);
    if (line === undefined) return;
    const name = applyNamePattern(pattern, i, line.name);
    if (name === line.name) return;
    Core.lines.patchLine(tx, { id, patch: { name } });
    changed++;
  });
  return changed;
};

/** N LINES draw order from an expression (`i`, `i+10`, `v*2`...) evaluated per line in export order. */
export const exprLinesZOrder: Cmd<
  { readonly lines: readonly LineId[]; readonly prog: ExprProgram; readonly seed: number },
  number
> = (tx, { lines, prog, seed }) => {
  const recs = lines.map((id) => tx.doc.lines.get(id)).filter((l): l is Line => l !== undefined);
  const n = recs.length;
  const rng = Expr.rng(seed);
  let changed = 0;
  recs.forEach((line, i) => {
    const out = Expr.apply(prog, { v: line.zOrder, i, n, u: n > 1 ? i / (n - 1) : 0 }, rng, tx.tempo);
    if (out === null || !Number.isFinite(out)) failExpr(tx, i, n);
    const z = Math.round(out);
    if (z === line.zOrder) return;
    Core.lines.patchLine(tx, { id: line.id, patch: { zOrder: z } });
    changed++;
  });
  return changed;
};

/** Same record patch on several lines. */
export const patchLines: Cmd<{
  readonly lines: readonly LineId[];
  readonly patch: RecordPatch<Pick<Line, 'name' | 'zOrder' | 'cover' | 'group' | 'rotateWithParent'>>;
}> = (tx, { lines, patch }) => {
  for (const id of lines) if (tx.doc.lines.has(id)) Core.lines.patchLine(tx, { id, patch });
};

/** Same editor-data patch (tag colour...) on several lines. */
export const patchLinesEditor: Cmd<{
  readonly lines: readonly LineId[];
  readonly patch: RecordPatch<LineEditorData>;
}> = (tx, { lines, patch }) => {
  for (const id of lines) if (tx.doc.lines.has(id)) Core.lines.patchLineEditor(tx, { id, patch });
};

/** Parent of several lines (each cycle-checked by Core.lines.setParent). */
export const setParentOfLines: Cmd<{
  readonly lines: readonly LineId[];
  readonly parent: LineId | null;
  readonly mode: 'keepNumbers' | 'keepPlace';
  readonly atSec: Sec;
}> = (tx, { lines, parent, mode, atSec }) => {
  for (const id of lines) {
    const line = tx.doc.lines.get(id);
    if (line === undefined || line.parent === parent) continue;
    Core.lines.setParent(tx, { line: id, parent, mode, atSec });
  }
};

/** Texture of several lines (the GIF rule applies per line). */
export const setTextureOfLines: Cmd<{ readonly lines: readonly LineId[]; readonly asset: AssetId | null }> = (
  tx,
  { lines, asset },
) => {
  for (const id of lines) {
    const line = tx.doc.lines.get(id);
    if (line === undefined || line.texture === asset) continue;
    Core.lines.setTexture(tx, { line: id, asset });
  }
};

/**
 * Type of several lines. Image / GIF lines need a texture (each line keeps its own when it already has a suitable
 * one, else `texture`); UI movers need an element (`attachUI`). Lines already of the type are left alone.
 */
export const setTypeOfLines: Cmd<{
  readonly lines: readonly LineId[];
  readonly type: LineTypeChoice;
  readonly texture?: AssetId | undefined;
  readonly attachUI?: UiElement | undefined;
}> = (tx, { lines, type, texture, attachUI }) => {
  for (const id of lines) {
    const line = tx.doc.lines.get(id);
    if (line === undefined) continue;
    if (lineTypeOf(tx.doc, tx.idx, line) === type) continue;
    const own = line.texture === null ? undefined : tx.doc.assets.get(line.texture);
    const ownFits = own !== undefined && (type === 'gif' ? isGifAsset(own) : isImageAssetRef(own));
    Core.lines.setLineType(tx, {
      line: id,
      type,
      ...((type === 'image' || type === 'gif') && !ownFits && texture !== undefined ? { texture } : {}),
      ...(type === 'uiMover' && attachUI !== undefined ? { attachUI } : {}),
    });
  }
};

/** Moves notes by `dt` (relative beat input on a mixed Time field). */
export const moveNotesBy: Cmd<{ readonly ids: readonly NoteId[]; readonly dt: Beat }> = (tx, { ids, dt }) => {
  const live = ids.filter((id) => tx.doc.notes.has(id));
  if (live.length === 0 || Beats.eq(dt, Beats.ZERO)) return;
  Core.notes.moveNotes(tx, { ids: live, dt, dx: 0 });
};

/**
 * Hold ends: absolute (`to`) or relative (`dt`). Every hold keeps at least `minLen` (one snap step); non-holds are
 * ignored.
 */
export const setHoldEnds: Cmd<{
  readonly ids: readonly NoteId[];
  readonly to?: Beat | undefined;
  readonly dt?: Beat | undefined;
  readonly minLen: Beat;
}> = (tx, { ids, to, dt, minLen }) => {
  const holds = ids.map((id) => tx.doc.notes.get(id)).filter((n): n is Note => n?.kind === 'hold');
  if (holds.length === 0) tx.fail({ key: m.noHolds });
  for (const n of holds) {
    let end = to ?? (dt === undefined ? n.end : Beats.add(n.end, dt, tx.rounding));
    const min = Beats.add(n.t, minLen, tx.rounding);
    if (Beats.lt(end, min)) end = min;
    if (Beats.eq(end, n.end)) continue;
    Core.notes.setNoteTimes(tx, { id: n.id, end });
  }
};

/** Moves keys by `dt` (relative beat input on a mixed At field). */
export const moveKeysBy: Cmd<{ readonly keys: readonly KeyId[]; readonly dt: Beat }> = (tx, { keys, dt }) => {
  const live = keys.filter((id) => tx.doc.keys.has(id));
  if (live.length === 0 || Beats.eq(dt, Beats.ZERO)) return;
  Core.keys.moveKeys(tx, { keys: live, dt });
};

/** Turns "Stay, then jump" on or off on every given segment (toggling only those that differ). */
export const setStay: Cmd<{ readonly segments: readonly KeyId[]; readonly on: boolean }> = (tx, { segments, on }) => {
  const flip = segments.filter((id) => {
    const k = tx.doc.keys.get(id);
    return k !== undefined && tx.idx.nextKey(id) !== undefined && (k.ease.kind === 'hold') !== on;
  });
  if (flip.length > 0) Core.keys.toggleHold(tx, { segments: flip });
};

/** Slice of tween segments ("Use part of the curve"); hold and Bezier segments are skipped (counted). */
export const setSlice: Cmd<
  { readonly segments: readonly KeyId[]; readonly left?: number; readonly right?: number },
  number
> = (tx, { segments, left, right }) => {
  let skipped = 0;
  for (const id of segments) {
    const k = tx.doc.keys.get(id);
    if (k === undefined) continue;
    if (k.ease.kind !== 'tween') {
      skipped++;
      continue;
    }
    const l = clamp01(left ?? k.ease.left);
    const r = clamp01(right ?? k.ease.right);
    if (!(l < r)) tx.fail({ key: m.sliceOrder });
    if (l === k.ease.left && r === k.ease.right) continue;
    Core.keys.setEase(tx, { segments: [id], ease: { kind: 'tween', id: k.ease.id, left: l, right: r } });
  }
  return skipped;
};

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

/** Properties the "Set at playhead" buttons of N LINES write. */
export type PlayheadGroup = 'alpha' | 'position' | 'rotate' | 'speed';

export function propsOfGroup(g: PlayheadGroup): readonly LineProp[] {
  return g === 'position' ? ['moveX', 'moveY'] : [g];
}

/**
 * "Set at playhead" on several lines: a keyframe at `t` on the property of every line (Core.keys.insertKeysAt,
 * shape-preserving; a missing track is created with its neutral value and the lead-in Stay key), then the given
 * values (raw units) on the new keys. Lines on which the property does not apply are skipped. Returns the keys.
 */
export const keyLinesAt: Cmd<
  {
    readonly lines: readonly LineId[];
    readonly props: readonly LineProp[];
    readonly layer: number;
    readonly t: Beat;
    readonly values?: Partial<Readonly<Record<LineProp, number>>>;
  },
  KeyId[]
> = (tx, { lines, props, layer, t, values }) => {
  const out: KeyId[] = [];
  for (const id of lines) {
    const line = tx.doc.lines.get(id);
    if (line === undefined) continue;
    const kind = kindOfLine(tx.doc, tx.idx, line);
    const usable = props.filter((p) => propAppliesTo(p, kind));
    if (usable.length === 0) continue;
    out.push(...Core.keys.insertKeysAt(tx, { line: id, props: usable, layer, t }));
    for (const p of usable) {
      const v = values?.[p];
      if (v === undefined) continue;
      const track = tx.idx.track(id, p, PROP_SPECS[p].layered ? layer : 0);
      const key = keyAt(trackKeys(tx.doc, tx.idx, track), t);
      if (key !== null) Core.keys.setKeyValues(tx, { keys: [key.id], part: 'both', value: v });
    }
  }
  if (out.length === 0) tx.fail({ key: m.setAtPlayheadNothing });
  tx.setFocus({ beat: t });
  return out;
};

/** Moves keys so that each lands on `to` (several keys on different tracks; clamped between neighbours). */
export const moveKeysTo: Cmd<{ readonly keys: readonly KeyId[]; readonly to: Beat }> = (tx, { keys, to }) => {
  for (const id of keys) {
    const k = tx.doc.keys.get(id);
    if (k === undefined || Beats.eq(k.t, to)) continue;
    Core.keys.moveKeys(tx, { keys: [id], dt: Beats.sub(to, k.t, tx.rounding) });
  }
};

/** Sets every note's time to `t` (holds keep their length). */
export const setNotesTime: Cmd<{ readonly ids: readonly NoteId[]; readonly t: Beat }> = (tx, { ids, t }) => {
  for (const id of ids) {
    const n = tx.doc.notes.get(id);
    if (n === undefined || Beats.eq(n.t, t)) continue;
    Core.notes.moveNotes(tx, { ids: [id], dt: Beats.sub(t, n.t, tx.rounding), dx: 0 });
  }
};

/**
 * Clamps the song-select preview into a song of `durationSec` (fatal PH-36 otherwise: Phira refuses to open the
 * chart). Returns false when the range already fits.
 */
export const clampPreview: Cmd<{ readonly durationSec: number }, boolean> = (tx, { durationSec }) => {
  const meta = tx.doc.meta;
  if (previewRangeIssue(meta.previewStart, meta.previewEnd, durationSec) === null) return false;
  const r = clampPreviewRange(meta.previewStart, meta.previewEnd, durationSec);
  Core.doc.patchMeta(tx, { patch: { previewStart: r.start, previewEnd: r.end } });
  return true;
};
